import type { FC, PropsWithChildren } from "react";

import {
  Box,
  Column,
  Row,
  Text,
  Heading,
  SuccessIcon,
  HtImage,
} from "@hightouch/htui";
import { Link } from "src/router";
import { useNavigate } from "src/router";

import { AuthLayout } from "src/components/auth/auth-layout";
import bgImage from "src/assets/backgrounds/signup.webp";
import { EmailButton, MicrosoftButton } from "src/components/auth/buttons";
import GoogleIdentityButton from "src/components/auth/google-identity-button";
import { Terms } from "src/components/auth/terms";
import { useAuthState } from "src/components/auth/use-auth-state";
import { PageSpinner } from "src/components/loading";
import { AuthCard } from "src/components/auth/auth-card";
import { buildReturnToUrl } from "src/utils/auth-redirect";

export const Signup: FC = () => {
  const navigate = useNavigate();
  const {
    returnTo,
    microsoftAuthUrl,
    isHandlingSuccessfulOauth,
    handleSuccessfulOauth,
    isMcpAuthFlow,
    partnerConnectionUUID,
  } = useAuthState();

  if (isHandlingSuccessfulOauth) {
    return <PageSpinner />;
  }

  return (
    <>
      <AuthLayout bgImage={bgImage} metaTitle="Sign up">
        <Row
          align="start"
          gap={24}
          justify={{ base: "center", xl: "space-between" }}
          maxW="1200px"
          w="100%"
        >
          <Column display={{ base: "none", xl: "flex" }} gap={10} flex={1}>
            <Box as={Heading} size="2xl" fontSize="48px" lineHeight="62.4px">
              Join thousands of companies who{" "}
              <Box as="span" color="teal.500">
                activate their data
              </Box>{" "}
              using Hightouch
            </Box>

            <Column as="ul" gap={4} p={0}>
              <ListItem>Generous free tier (no credit card required)</ListItem>
              <ListItem>Easy setup, plus 24/7 technical support</ListItem>
              <ListItem>
                {import.meta.env.VITE_SOURCES_COUNT}+ data sources and{" "}
                {import.meta.env.VITE_DESTINATIONS_COUNT}+ destinations
              </ListItem>
            </Column>

            <Logos />
          </Column>

          <AuthCard heading="Sign up for Hightouch">
            <Column gap={4}>
              <GoogleIdentityButton
                action="Sign up"
                onSuccess={handleSuccessfulOauth}
                partnerConnectionUUID={partnerConnectionUUID}
              />
              <MicrosoftButton
                action="Sign up"
                onClick={() => {
                  window.location.href = microsoftAuthUrl;
                }}
              />
              <EmailButton
                action="Sign up"
                onClick={() => {
                  navigate(
                    returnTo
                      ? buildReturnToUrl("/signup/email", returnTo)
                      : "/signup/email",
                  );
                }}
              />
            </Column>

            <Column gap={2}>
              {isMcpAuthFlow ? (
                <Text fontWeight="medium">
                  Existing accounts can continue with the same sign-in options
                  above.
                </Text>
              ) : (
                <Box>
                  <Text fontWeight="medium">Already have an account?</Text>{" "}
                  <Link href="/login">Sign in</Link>
                </Box>
              )}
              <Terms />
            </Column>
          </AuthCard>
        </Row>
      </AuthLayout>
    </>
  );
};

function ListItem({ children }: PropsWithChildren) {
  return (
    <Row as="li" align="center" gap={2}>
      <Box as={SuccessIcon} fontSize="20px" color="teal.500" />
      <Text fontWeight="medium" color="text.secondary">
        {children}
      </Text>
    </Row>
  );
}

type Customer = {
  name: string;
  logo: string;
};

export const customers: Customer[] = [
  {
    name: "Spotify",
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/c79ef630644336a446eff2ccdc7afded6cd54c02-240x72.svg",
  },
  {
    name: "Warner Music Group",
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/84dbb616e064de970801b02e16e419d60fcd4b44-2560x1009.png",
  },
  {
    name: "PetSmart",
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/375df10710b9b2fbd2cf9c2ae5f669672753022d-607x150.png",
  },
  {
    name: "Ramp",
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/acaf90882a1ded8a3796cc3972219355a42d009c-270x72.svg",
  },
  {
    name: "Aritzia",
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/ffef00131ab19307fec4fa82af64da21a9eaf77b-2138x422.svg",
  },
  {
    name: "Dominos",
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/8f11d589caa305eb61f3fe2511fa75ca40acf82e-667x148.png",
  },
];

const Icon = ({ src, alt }: { src: string; alt: string }) => (
  <HtImage
    src={`${src}?w=200`}
    alt={alt}
    sx={{
      objectFit: "contain",
      objectPosition: "center",
      height: "100%",
      width: "100%",
      maxWidth: "72px",
      filter: "brightness(0)",
      userSelect: "none",
    }}
  />
);

function Logos() {
  return (
    <Column gap={6} w="100%">
      <Text color="text.secondary">Trusted by teams at</Text>
      <Row align="center" gap={8} height="28px" w="100%" maxW="100%">
        {customers.map((customer) => (
          <Icon key={customer.name} src={customer.logo} alt={customer.name} />
        ))}
      </Row>
    </Column>
  );
}
